import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SendIcon, MessageCircleIcon, PhoneIcon, MailIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
interface FormData {
  name: string;
  position: string;
  phone: string;
  email: string;
}
interface FormErrors {
  name?: string;
  position?: string;
  phone?: string;
  email?: string;
}
interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}
function getUtmParams(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
    utm_medium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
    utm_term: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
    utm_content: params.get('utm_content') || sessionStorage.getItem('utm_content') || ''
  };
}
export function ContactForm() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    position: '',
    phone: '',
    email: ''
  });
  const [utmParams, setUtmParams] = useState<UtmParams>({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  useEffect(() => {
    const utms = getUtmParams();
    setUtmParams(utms);
    Object.entries(utms).forEach(([key, value]) => {
      if (value) sessionStorage.setItem(key, value);
    });
  }, []);
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = t.contact.errors.name;
    if (!formData.position.trim()) newErrors.position = t.contact.errors.position;
    if (!formData.phone.trim()) {
      newErrors.phone = t.contact.errors.phone_required;
    } else if (!/^[+\d\s()-]{7,}$/.test(formData.phone)) {
      newErrors.phone = t.contact.errors.phone_invalid;
    }
    if (formData.email.trim() && !/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      newErrors.email = t.contact.errors.email_invalid;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/mdalrzke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          position: formData.position,
          phone: formData.phone,
          email: formData.email,
          ...utmParams
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        console.error('Formspree error:', data);
      }
    } catch (err) {
      console.error('Network error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    if (errors[field]) setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" id="contact" data-id="element-136" data-mp="34hbajnwpzcm9">
      <div className="absolute inset-0 pointer-events-none" data-id="element-137" data-mp="3jqy9jie4u9by">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary-50/30 blur-3xl hidden sm:block" data-id="element-138" data-mp="3exvmzu7rw059" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-cyan-50/20 blur-3xl hidden sm:block" data-id="element-139" data-mp="3axuu54jxfmeo" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-140" data-mp="6j4tvdbk28zd">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-141" data-mp="2ht1x157lbo0">
          {/* Left: info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-142" data-mp="3tnufphgk6jdc">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-143" data-mp="2d89qowe642zm">
              {t.contact.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-144" data-mp="2847jnna45zuw">
              {t.contact.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-145" data-mp="2d1qzgrmgmkoy">
              {t.contact.subtitle}
            </p>

            <div className="mt-10 space-y-5" data-id="element-146" data-mp="2lw5k79prenfg">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-navy-50/80 to-transparent border border-navy-100/50" data-id="element-147" data-mp="2lvcdg8qsvxxb">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center shadow-lg shadow-primary-500/15" data-id="element-148" data-mp="2vubnnvr7s1mf">
                  <PhoneIcon className="w-5 h-5" data-id="element-149" data-mp="30rbs628f0wbf" />
                </div>
                <div data-id="element-150" data-mp="11hlq941n0aaa">
                  <div className="text-xs text-navy-400 font-semibold uppercase tracking-wide" data-id="element-151" data-mp="11idf7w0qb9rl">
                    {t.contact.phone_label}
                  </div>
                  <div className="flex flex-col" data-id="element-152" data-mp="yfquqd1yw16f">
                    <a href="tel:+380668356279" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-153" data-mp="tfm968m7cg9c">
                      +380 66 835 62 79
                    </a>
                    <a href="tel:+380960465744" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-154" data-mp="13c7eyw61pve8">
                      +380 96 046 57 44
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-navy-50/80 to-transparent border border-navy-100/50" data-id="element-155" data-mp="1r5fsmhjdrl3t">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center shadow-lg shadow-primary-500/15" data-id="element-156" data-mp="2039oxsh5aeqz">
                  <MailIcon className="w-5 h-5" data-id="element-157" data-mp="2eqzzhysexef3" />
                </div>
                <div data-id="element-158" data-mp="2eongdiax4y0g">
                  <div className="text-xs text-navy-400 font-semibold uppercase tracking-wide" data-id="element-159" data-mp="2jpkzbx3r5w30">
                    {t.contact.email_label}
                  </div>
                  <a href="mailto:sales@e-s-c-a.com" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-160" data-mp="25psdog73261w">
                    sales@e-s-c-a.com
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10" data-id="element-161" data-mp="20t1uhjzzeuc9">
              <p className="text-sm font-semibold text-navy-500 mb-4" data-id="element-162" data-mp="1n03utlgiag2w">
                {t.contact.messenger_label}
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-163" data-mp="1r1p5d05kii9x">
                <a href="https://t.me/sergeydenisyuk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#229ED9]/10 text-[#229ED9] font-semibold text-sm hover:bg-[#229ED9]/20 transition-colors border border-[#229ED9]/15" data-id="element-164" data-mp="1d1o61p4ybiwk">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-165" data-mp="1shb9ibz5gc4j" />
                  Telegram
                </a>
                <a href="viber://chat?number=380668356279" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#7360F2]/10 text-[#7360F2] font-semibold text-sm hover:bg-[#7360F2]/20 transition-colors border border-[#7360F2]/15" data-id="element-166" data-mp="1ojnd14ht6baj">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-167" data-mp="14u36k0p71f5o" />
                  Viber
                </a>
                <a href="https://wa.me/380668356279" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366]/10 text-[#25D366] font-semibold text-sm hover:bg-[#25D366]/20 transition-colors border border-[#25D366]/15" data-id="element-168" data-mp="10u2tokhqtc0q">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-169" data-mp="37ffm8fei2h7b" />
                  WhatsApp
                </a>
              </div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="mt-10 flex items-center gap-3 p-4 rounded-xl bg-emerald-50/60 border border-emerald-100" data-id="element-170" data-mp="3cdh10o4w7ucz">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-171" data-mp="2117xtcw9csjx" />
              <p className="text-sm text-emerald-700 font-medium" data-id="element-172" data-mp="251rukfcx5u5j">
                {t.contact.trust_badge}
              </p>
            </motion.div>
          </motion.div>

          {/* Right: form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-173" data-mp="3khabfaholwlp">
            {isSubmitted ? <div className="flex flex-col items-center justify-center h-full text-center p-8 rounded-2xl bg-gradient-to-b from-white to-emerald-50/30 border border-emerald-100 shadow-lg shadow-emerald-100/20" data-id="element-174" data-mp="30uufrmx1eugh">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20" data-id="element-175" data-mp="3al6yd9lakdjz">
                  <CheckCircleIcon className="w-8 h-8" data-id="element-176" data-mp="2i20bbmx1ikqi" />
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-177" data-mp="2akbsz9bn3muk">
                  {t.contact.success_title}
                </h3>
                <p className="text-navy-500" data-id="element-178" data-mp="25l8p3s7ebmo6">{t.contact.success_subtitle}</p>
                <button onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: '',
                position: '',
                phone: ''
              });
            }} className="mt-6 text-primary-600 font-semibold hover:text-primary-700 transition-colors" data-id="element-179" data-mp="20kl2diq79rzr">
                  {t.contact.send_another}
                </button>
              </div> : <form onSubmit={handleSubmit} className="p-5 sm:p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-white to-navy-50/40 border border-navy-200/60 shadow-xl shadow-navy-200/20" noValidate data-id="element-180" data-mp="1cxctyrxh62ti">
                <h3 className="text-lg font-bold text-navy-900 mb-6" data-id="element-181" data-mp="u7zn6mu5qqs9">
                  {t.contact.form_title}
                </h3>
                <div className="space-y-5" data-id="element-182" data-mp="yae9jgx1c8oa">
                  <div data-id="element-183" data-mp="y705834t9pe7">
                    <label htmlFor="name" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-184" data-mp="ka3f63wiobyd">
                      {t.contact.name_label}
                    </label>
                    <input id="name" type="text" value={formData.name} onChange={handleChange('name')} placeholder={t.contact.name_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.name ? 'border-red-400' : 'border-navy-200'}`} data-id="element-185" data-mp="1cyr3y9gh5jgo" />
                    {errors.name && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-186" data-mp="3lcnt3oilnxbt">
                        {errors.name}
                      </p>}
                  </div>

                  <div data-id="element-187" data-mp="3u592wf496toi">
                    <label htmlFor="position" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-188" data-mp="3u8n3bhtcwstb">
                      {t.contact.position_label}
                    </label>
                    <input id="position" type="text" value={formData.position} onChange={handleChange('position')} placeholder={t.contact.position_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.position ? 'border-red-400' : 'border-navy-200'}`} data-id="element-189" data-mp="2xh1oa5dphfjy" />
                    {errors.position && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-190" data-mp="1m0ijbdxj6ku2">
                        {errors.position}
                      </p>}
                  </div>

                  <div data-id="element-191" data-mp="1vyy0op6rafqd">
                    <label htmlFor="phone" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-192" data-mp="1vzhfvy50uq0p">
                      {t.contact.phone_form_label}
                    </label>
                    <input id="phone" type="tel" value={formData.phone} onChange={handleChange('phone')} placeholder={t.contact.phone_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.phone ? 'border-red-400' : 'border-navy-200'}`} data-id="element-193" data-mp="29p1j152ilpaj" />
                    {errors.phone && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-194" data-mp="30j1dux4pi88">
                        {errors.phone}
                      </p>}
                  </div>

                  <div data-id="element-195" data-mp="vqhr7sj5djdy">
                    <label htmlFor="email" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-196" data-mp="vookyecg516h">
                      {t.contact.email_form_label}
                    </label>
                    <input id="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange('email')} placeholder={t.contact.email_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.email ? 'border-red-400' : 'border-navy-200'}`} data-id="element-197" data-mp="h13j0nrb5805" />
                    {errors.email && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-198" data-mp="2z5lekcfcluor">
                        {errors.email}
                      </p>}
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-primary-600/30 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-199" data-mp="2pa9zft012lgh">
                  {isSubmitting ? <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-200" data-mp="jm17amhh9njr" />
                      {t.contact.submitting}
                    </> : <>
                      <SendIcon className="w-4 h-4" data-id="element-201" data-mp="sle7spnmj1sc" />
                      {t.contact.submit}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-202" data-mp="179sjlq30q0l1" />
                    </>}
                </button>
                <p className="mt-4 text-xs text-navy-400 text-center" data-id="element-203" data-mp="12br2vbt2edcq">
                  {t.contact.privacy}
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}